import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../css/GeneComponent.css';
import { FaDownload, FaDna } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';

const PAGE_SIZE = 20;

const GeneComponent = () => {
  const [genes, setGenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [downloadFormat, setDownloadFormat] = useState('xml');
  const [selectedGenes, setSelectedGenes] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('all');
  const navigate = useNavigate();
  // Track expanded genes
  const [expandedGeneIds, setExpandedGeneIds] = useState([]);

  // Determine database source
  const getDatabaseSource = (gene) => {
    if (gene.source_database) return gene.source_database;
    const geneIdStr = String(gene.gene_id || '');
    return geneIdStr.startsWith('Et_') ? 'NCBI' : 'Ensembl';
  };

  const fetchGeneData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (sourceFilter !== 'all') params.source = sourceFilter;

      const response = await axios.get(
        'http://197.156.76.122/api/gene/',
        { params }
      );

      if (response.data && Array.isArray(response.data.results)) {
        setGenes(response.data.results);
        setTotalCount(response.data.count || 0);
        setTotalPages(Math.ceil((response.data.count || 0) / PAGE_SIZE));
      } else {
        setError('Unexpected API response structure');
      }
    } catch (err) {
      console.error('API Error:', err);
      setError('Failed to fetch gene data');
    } finally {
      setLoading(false);
    }
  }, [page, sourceFilter]);

  useEffect(() => {
    fetchGeneData();
  }, [fetchGeneData]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) setPage(newPage);
  };

  const handleDownload = () => {
    if (!selectedGenes.length) {
      alert('Please select at least one gene to download.');
      return;
    }

    const formatEndpoints = {
      csv: 'http://197.156.76.122/api/gene/download/csv/',
      xml: 'http://197.156.76.122/api/gene/download/xml/',
      summary: 'http://197.156.76.122/api/api/gene/download/summary/',
      tsv: 'http://197.156.76.122/api/gene/download/tsv/',
    };

    const downloadUrl = `${formatEndpoints[downloadFormat]}?genes=${selectedGenes.join(',')}`;
    window.open(downloadUrl, '_blank');
  };

  const handleCheckboxChange = (geneId) => {
    setSelectedGenes((prev) =>
      prev.includes(geneId)
        ? prev.filter((id) => id !== geneId)
        : [...prev, geneId]
    );
  };

  const toggleGeneMetadata = (geneId) => {
    setExpandedGeneIds(prev =>
      prev.includes(geneId)
        ? prev.filter(id => id !== geneId)
        : [...prev, geneId]
    );
  };

  // Render metadata + NCBI related info
  const renderMetadata = (gene) => {
    const metadataFields = [
      { label: 'Gene ID', value: gene.gene_id },
      { label: 'Symbol', value: gene.symbol },
      { label: 'Locus Tag', value: gene.locus_tag },
      { label: 'Aliases', value: gene.aliases },
      { label: 'Chromosome', value: gene.chromosome },
      { label: 'Location', value: gene.location_start && gene.location_end ? `${gene.location_start}..${gene.location_end}` : null },
      { label: 'Strand', value: gene.strand },
      { label: 'Source Database', value: gene.source_database },
      { label: 'Genomic Accession', value: gene.genomic_accession },
      { label: 'Description', value: gene.description },
    ];

    const source = getDatabaseSource(gene);
    const ncbiUrl = gene.gene_id ? `https://www.ncbi.nlm.nih.gov/gene/${gene.gene_id}` : null;

    return (
      <div className="gene-metadata">
        {metadataFields.map((field, idx) =>
          field.value ? (
            <p key={idx}><strong>{field.label}:</strong> {field.value}</p>
          ) : null
        )}

        {/* NCBI specific section */}
        {source === 'NCBI' && ncbiUrl && (
          <div className="ncbi-section">
            <p><strong>NCBI Gene Page:</strong> <a href={ncbiUrl} target="_blank" rel="noreferrer">{ncbiUrl}</a></p>
            {gene.organism && <p><strong>Organism:</strong> {gene.organism}</p>}
            {gene.summary && <p><strong>Summary:</strong> {gene.summary}</p>}
            {gene.chromosome && <p><strong>Chromosome Location:</strong> {gene.chromosome}</p>}
            {gene.map_location && <p><strong>Map Location:</strong> {gene.map_location}</p>}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div className="loading-message">Loading gene data...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="gene-page">
      {/* Back Button */}
	  <button 
	    className="back-button"
	    onClick={() => navigate('/')}
	  >
	    <FaArrowLeft /> Back to Home
	  </button>
      <div className="taxonomy-box">
        <h3>
          <a href="http://197.156.76.122/taxonomy" target="_blank" rel="noreferrer">
            <u>Taxonomy: Eragrostis tef</u>
          </a>
        </h3>
        <p>
          <strong><i>Eragrostis tef</i></strong> is a cereal grass native to Ethiopia.
          It is gluten-free, climate-resilient, and rich in nutrients.
        </p>
        <p>
          <strong>Taxonomy ID:</strong>{' '}
          <a href="http://197.156.76.122/taxonomy" target="_blank" rel="noreferrer">
            110835
          </a>{' '}
          | <strong>Genomes:</strong>{' '}
          <a href="http://197.156.76.122/genomeassembly" target="_blank" rel="noreferrer">
            View
          </a>
        </p>
      </div>

      <div className="header-row">
        <h2><FaDna /> Gene Dataset</h2>
        <div className="controls">
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            <option value="all">All Sources</option>
            <option value="NCBI">NCBI</option>
            <option value="Ensembl">Ensembl</option>
          </select>
        </div>
      </div>

      <div className="results-meta">
        Showing <strong>{(page - 1) * PAGE_SIZE + 1}</strong> to{' '}
        <strong>{Math.min(page * PAGE_SIZE, totalCount)}</strong> of{' '}
        <strong>{totalCount}</strong>
      </div>

      <table className="gene-table">
        <thead>
          <tr>
            <th>Name / Gene ID</th>
            <th>Database Source</th>
            <th>Location</th>
            <th>Aliases</th>
            <th>Strand</th>
          </tr>
        </thead>
        <tbody>
          {genes.map((gene) => {
            const source = getDatabaseSource(gene);
            const isSelected = selectedGenes.includes(gene.gene_id);
            const locationLabel =
              gene.location_start && gene.location_end
                ? `${gene.chromosome || gene.genomic_accession || 'Unplaced'} (${gene.location_start}..${gene.location_end}${gene.strand === '-' ? ', complement' : ''})`
                : 'Unknown';

            return (
              <tr key={gene.gene_id} className={isSelected ? 'selected-row' : ''}>
                <td>
                  <span
                    style={{ cursor: 'pointer', color: '#1a73e8' }}
                    onClick={() => toggleGeneMetadata(gene.gene_id)}
                  >
                    {gene.symbol || gene.locus_tag || '-'}
                  </span>

                  {expandedGeneIds.includes(gene.gene_id) && renderMetadata(gene)}

                  {source === 'NCBI' && <br />}
                  {source === 'NCBI' && <small>ID: {gene.gene_id}</small>}
                </td>
                <td>{source}</td>
                <td>{locationLabel}</td>
                <td>{gene.aliases || gene.locus_tag || '-'}</td>
                <td>{gene.strand}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="pagination">
        <button onClick={() => handlePageChange(1)} disabled={page === 1}>« First</button>
        <button onClick={() => handlePageChange(page - 1)} disabled={page === 1}>‹ Prev</button>
        <span>Page {page} of {totalPages}</span>
        <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>Next ›</button>
        <button onClick={() => handlePageChange(totalPages)} disabled={page === totalPages}>Last »</button>
      </div>
    </div>
  );
};

export default GeneComponent;

